// Profile dropdown in the admin header:
//   avatar → opens a card with the user's info, company-logo upload,
//   and a clearly labelled Sign out button.
import React from 'react';
import { Icon } from './ui.jsx';
import { api } from './api.js';

export function ProfileMenu({ user, tenant, onLogout, onTenantUpdated }) {
  const [open, setOpen] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState(null);
  const fileRef = React.useRef(null);
  const panelRef = React.useRef(null);

  const isAdmin = user?.role === 'admin';

  // Close the dropdown on outside click / Escape.
  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Read a File as a base64 data URL. Caps at ~500 KB.
  const readAsDataUrl = (file) => new Promise((resolve, reject) => {
    if (file.size > 500 * 1024) {
      reject(new Error('File is too large — please upload an image under 500 KB.'));
      return;
    }
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(fr.error || new Error('Could not read file.'));
    fr.readAsDataURL(file);
  });

  const onFile = async (e) => {
    setUploadError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setUploadError('Please pick an image file (PNG / JPG / SVG).'); return; }
    setUploading(true);
    try {
      const dataUrl = await readAsDataUrl(file);
      const updated = await api.tenants.update(tenant.id, { logoUrl: dataUrl });
      if (onTenantUpdated) onTenantUpdated(updated);
    } catch (err) {
      setUploadError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
      // Reset the input so picking the same file twice still fires change.
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const clearLogo = async () => {
    if (!confirm('Remove the company logo?')) return;
    setUploading(true); setUploadError(null);
    try {
      const updated = await api.tenants.update(tenant.id, { logoUrl: null });
      if (onTenantUpdated) onTenantUpdated(updated);
    } catch (err) {
      setUploadError(err.message || 'Remove failed.');
    } finally { setUploading(false); }
  };

  const initials = (user?.name || '?').split(' ').map(s => s[0]).filter(Boolean).join('').slice(0, 2).toUpperCase();

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 h-9 px-2 rounded-md hover:bg-neutral-100 transition"
      >
        <div className="w-7 h-7 rounded-full bg-primary-100 text-primary-900 flex items-center justify-center text-[11px] font-bold">{initials}</div>
        <div className="leading-tight text-left pr-1">
          <div className="font-semibold text-neutral-900 text-[13px]">{user?.name || '—'}</div>
          <div className="text-[11px] text-neutral-500 capitalize">{user?.role || ''}</div>
        </div>
        <Icon name="chevronDown" size={14} className={`text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`}/>
      </button>

      {open && (
        <div className="absolute top-[calc(100%+8px)] right-0 w-[320px] bg-white rounded-lg shadow-2xl border border-neutral-200 z-[60] overflow-hidden">
          {/* Header — user info */}
          <div className="px-4 py-3 border-b border-neutral-200 bg-canvas/60">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-900 flex items-center justify-center text-[12px] font-bold">{initials}</div>
              <div className="min-w-0">
                <div className="font-semibold text-neutral-900 text-[13px] truncate">{user?.name}</div>
                <div className="text-[11px] text-neutral-500 truncate">{user?.email}</div>
              </div>
            </div>
          </div>

          {/* Company logo section — admin only */}
          {isAdmin && tenant && (
            <div className="px-4 py-3 border-b border-neutral-200">
              <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 mb-2">Company logo</div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-md border border-neutral-200 bg-white flex items-center justify-center overflow-hidden">
                  {tenant.logoUrl
                    ? <img src={tenant.logoUrl} alt="Company logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }}/>
                    : <span className="text-[10px] text-neutral-400">No logo</span>}
                </div>
                <div className="flex-1">
                  <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFile}/>
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="w-full h-8 px-3 text-[12px] font-semibold rounded-md bg-neutral-900 hover:bg-black text-white disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
                  >
                    <Icon name="upload" size={12}/>
                    {uploading ? 'Uploading…' : (tenant.logoUrl ? 'Replace logo' : 'Upload logo')}
                  </button>
                  {tenant.logoUrl && !uploading && (
                    <button onClick={clearLogo} className="mt-1.5 text-[11px] text-neutral-500 hover:text-red-700">
                      Remove logo
                    </button>
                  )}
                </div>
              </div>
              {uploadError && <div className="mt-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">{uploadError}</div>}
              <div className="text-[10.5px] text-neutral-500 mt-2 leading-relaxed">
                PNG / JPG / SVG · Max 500 KB · Square works best.
              </div>
            </div>
          )}

          {/* Sign out — prominent */}
          <div className="px-4 py-3">
            <button
              onClick={() => { setOpen(false); onLogout?.(); }}
              className="w-full h-10 rounded-md bg-white border border-neutral-200 hover:border-red-300 hover:bg-red-50 hover:text-red-700 text-[13px] font-semibold text-neutral-800 inline-flex items-center justify-center gap-2 transition"
            >
              <Icon name="unlock" size={14}/>
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
